<template>
  <tailwind-layout title="Payment Information">
    <template #header>
      <div class="mb-4 px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
        <h1 class="font-semibold text-2xl leading-tight">Payment Information</h1>
        <div class="flex items-center space-x-4">
          <inertia-link
            class="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
            :href="route('rented-properties.payments', pay_data.rented_property_id)">
            <FontAwesome :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
          </inertia-link>
        </div>
      </div>
    </template>
    <div class="">
      <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
        <div class="bg-white pt-2 pb-16 px-4 overflow-hidden sm:px-6 lg:px-8 lg:py-8">
          <div class="relative max-w-xl mx-auto">
            <svg class="absolute left-full transform translate-x-1/2" width="404" height="404" fill="none"
              viewBox="0 0 404 404" aria-hidden="true">
              <defs>
                <pattern id="85737c0e-0916-41d7-917f-596dc7edfa27" x="0" y="0" width="20" height="20"
                  patternUnits="userSpaceOnUse">
                  <rect x="0" y="0" width="4" height="4" class="text-gray-200" fill="currentColor" />
                </pattern>
              </defs>
              <rect width="404" height="404" fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)" />
            </svg>
            <svg class="absolute right-full bottom-0 transform -translate-x-1/2" width="404" height="404" fill="none"
              viewBox="0 0 404 404" aria-hidden="true">
              <defs>
                <pattern id="85737c0e-0916-41d7-917f-596dc7edfa27" x="0" y="0" width="20" height="20"
                  patternUnits="userSpaceOnUse">
                  <rect x="0" y="0" width="4" height="4" class="text-gray-200" fill="currentColor" />
                </pattern>
              </defs>
              <rect width="404" height="404" fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)" />
            </svg>
            <div class="mt-1">
              <form @submit.prevent="store" class="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8">

                <div class="sm:col-span-2 text-center text-gray-600">

                  <div v-if="pay_data.value !== 'all'" class="flex space-x-4 mt-2">
                    <div class="w-1/2">
                      <label for="rent" class="text-left font-bold"
                        v-bind:class="(form.errors.amount) ? 'block text-sm font-medium text-red-500' : 'block text-sm font-medium'">
                        Enter Amount to Pay <span class="text-red-700">*</span>
                      </label>

                      <div class="mt-1 relative rounded-md">
                        <div class="absolute left-0 pl-3 pointer-events-none" :class="!form.errors.amount ? 'mt-2' : ''"
                          v-bind:class="(form.errors.amount) ? 'top-3' : 'inset-y-0'">
                          <font-awesome-icon :icon="faDollarSign" class="text-gray-400" />
                        </div>
                        <input id="amount" v-model="form.amount" type="number" step="0.01"
                          v-bind:class="(form.errors.amount) ? 'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10' : 'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'">
                        <p class="text-gray-500 text-xs text-left">Doesn't Include Convenience Fee</p>
                        <p v-text="form.errors.amount" class="text-red-500 text-xs text-left"></p>
                      </div>
                    </div>
                    <div class="w-1/2">
                      <label for="rent" class="text-left font-bold"
                        v-bind:class="(form.errors.remaining) ? 'block text-sm font-medium text-red-500' : 'block text-sm font-medium'">
                        Remaining Amount
                      </label>

                      <div class="mt-1 relative rounded-md">
                        <div class="absolute left-0 pl-3 flex items-center pointer-events-none"
                          v-bind:class="(form.errors.remaining) ? 'top-3' : 'inset-y-0'">
                          <font-awesome-icon :icon="faDollarSign" class="text-gray-400" />
                        </div>
                        <input id="remaining" v-model="form.remaining" type="number" step="0.01" :disabled="true"
                          v-bind:class="(form.errors.remaining) ? 'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10 disabled:bg-gray-100' : 'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10 disabled:bg-gray-100'">
                        <p v-text="form.errors.remaining" class="text-red-500 text-sm text-left"></p>
                      </div>
                    </div>
                  </div>

                  <table class="w-full mt-5">
                    <tbody>
                      <tr>
                        <td class="font-bold text-left border">Payment Description</td>
                        <td class="text-left p-1 border">{{ pay_data['title'] }}</td>
                      </tr>
                      <tr v-if="pay_data.value !== 'all'">
                        <td class="font-bold text-left border">Actual Amount</td>
                        <td class="text-left p-1 border">{{ formatCurrency(pay_data.actual_amount) }}</td>
                      </tr>
                      <tr v-if="pay_data['extra_charges']['amount'] > 0">
                        <td class="font-bold text-left border">Amount</td>
                        <td class="text-left p-1 border">{{ formatCurrency(form.amount) }}</td>
                      </tr>
                      <tr v-for="one_time_charge in pay_data.one_time_charges" :key="one_time_charge.id">
                        <td class="text-left border font-bold">{{ one_time_charge.title }}</td>
                        <td class="text-left p-1 border">${{ one_time_charge.remaining }}</td>
                      </tr>
                      <tr v-if="pay_data['extra_charges']['amount'] > 0">
                        <td class="font-bold text-left border">Extra Charges</td>
                        <td class="text-left p-1 border">{{ formatCurrency(pay_data['extra_charges']['amount']) }}</td>
                      </tr>
                      <tr v-if="pay_data['extra_charges']['amount'] > 0">
                        <td class="font-bold text-left border">Extra Charges Reason</td>
                        <td class="text-left p-1 border">{{ pay_data['extra_charges']['reason'] }}</td>
                      </tr>
                      <tr v-if="pay_data['extra_charges']['amount'] > 0">
                        <td class="font-bold text-left border">Extra Days</td>
                        <td class="text-left p-1 border">{{ pay_data['extra_charges']['overdue_days'] }}</td>
                      </tr>
                      <tr>
                        <td class="font-bold text-left border">{{ pay_data.value !== 'all' ? 'Pay Amount' : 'Sub Total'
                          }}
                        </td>
                        <td class="text-left p-1 border">${{ form.subtotal }}</td>
                      </tr>
                      <tr>
                        <td class="font-bold text-left border">Convenience Fee</td>
                        <td class="text-left p-1 border">
                          <template v-if="pay_data.is_ach_fee_waived">
                            <span class="line-through text-gray-400">${{ originalPlaidFee }}</span>
                            <span class="text-green-600 ml-1">$0.00</span>
                            <span class="text-xs text-green-600 ml-1">(Waived - RiskGuard Pro)</span>
                          </template>
                          <template v-else>
                            ${{ plaid_fee }}
                          </template>
                        </td>
                      </tr>
                      <tr>
                        <td class="font-bold text-left border">Total to Pay</td>
                        <td class="text-left p-1 border text-2xl font-bold">${{ total }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p v-text="form.errors.rented_property_id" class="text-red-500 text-sm"></p>
                  <p v-text="form.errors.type" class="text-red-500 text-sm"></p>
                  <p v-text="form.errors.value" class="text-red-500 text-sm"></p>

                  <div class="text-sm mt-3">(This is the amount that will be charged to your bank account)</div>
                  
                  <div v-if="!is_kyc && link_tokens.idv">
                    <div class="rounded-md bg-yellow-50 p-4 mt-4">
                      <div class="flex">
                        <div class="flex-shrink-0">
                          <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor"
                            aria-hidden="true">
                            <path fill-rule="evenodd"
                              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                              clip-rule="evenodd"></path>
                          </svg>
                        </div>
                        <div class="ml-3">
                          <h3 class="text-sm font-medium text-yellow-800 text-left">Identity Verification Pending</h3>
                          <div class="mt-2 text-sm text-yellow-700">
                            <button @click="initializePlaidLinkIdv(link_tokens.idv)" class="text-blue-600">Click here to
                              securely verify your identity and proceed with your rent payment.</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                    <div v-else class="mt-4 text-left">
                    <div v-if="!user_bank_account">
                      <div v-show="showPlaidContainer" ref="plaidContainer" class="h-[400px] mt-2"></div>
                    </div>
                    <div v-else>
                      <!-- Show warning if account needs update -->
                      <div v-if="user_action_required || user_bank_account.status === 'needs_update'" class="rounded-md bg-yellow-50 p-4 mb-4 border border-yellow-200">
                        <div class="flex">
                          <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd"
                                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                                clip-rule="evenodd"></path>
                            </svg>
                          </div>
                          <div class="ml-3">
                            <h3 class="text-sm font-medium text-yellow-800">Bank Account Update Required</h3>
                            <div class="mt-2 text-sm text-yellow-700">
                              <p>Your bank account needs to be updated. Please 
                                <inertia-link :href="route('user-bank-accounts.index')" class="font-semibold underline hover:text-yellow-800">
                                  update your account on the Bank Accounts page
                                </inertia-link>
                                to proceed with payment.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="bg-indigo-100 border border-r p-4">
                        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                          <!-- Bank Info -->
                          <div class="mb-4 sm:mb-0">
                            <p class="text-sm font-semibold leading-6 text-gray-900">Bank Account</p>
                            <div class="mt-3 space-y-6 sm:space-y-0">
                              <div class="flex items-center">
                                <input id="bank_account_id" type="radio" checked
                                  class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600">
                                <label for="bank_account_id"
                                  class="ml-3 block text-sm font-medium leading-6 text-gray-900">
                                  {{ user_bank_account.bank_name }} (**** **** **** {{ user_bank_account.account_last_4
                                  }})
                                  <span v-if="user_bank_account.status === 'needs_update'"
                                    class="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-yellow-50 text-yellow-700 ring-yellow-600/20 ml-2">
                                    Needs Update
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>

                          <!-- Buttons -->
                          <div class="flex flex-col items-center sm:flex-row sm:space-x-2">
                            <inertia-link :href="route('user-bank-accounts.index')"
                              class="w-1/2 sm:w-auto bg-gray-700 text-white sm:bg-white sm:text-gray-900 px-4 py-2 text-sm font-semibold rounded ring-1 ring-inset ring-gray-300 hover:bg-gray-700 hover:text-white mt-2 sm:mt-0 text-center">
                              Change Account
                            </inertia-link>
                          </div>
                        </div>
                      </div>

                      <div class="relative flex items-start mt-2">
                        <div class="flex h-6 items-center">
                          <input id="customer_mandate" v-model="form.customer_mandate"
                            aria-describedby="customer-mandate-description" type="checkbox"
                            class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600">
                        </div>
                        <div class="ml-2 text-sm leading-6">
                          <label for="customer_mandate" class="font-medium text-gray-900">
                            You, <span class="font-semibold">{{ $page.props.auth.user.name }}</span>, authorize <span
                              class="font-semibold">"RentRisk LLC</span>. to initiate an ACH debit from your provided
                            bank
                            account in accordance with our terms on behalf of your landlord <span
                              class="font-semibold">{{
                              pay_data.landlord.name }}</span>. You agree to the one-time payment of <span
                              class="font-semibold">${{ total }}</span> on <span class="font-semibold">{{ today_date
                              }}</span>.
                          </label>
                        </div>
                      </div>
                      <p v-text="form.errors.customer_mandate" class="text-red-500 text-xs text-left"></p>
                    </div>
                  </div>
                  <!-- Helpful note about managing accounts -->
                  <div v-if="!user_bank_account" class="mt-3 text-sm text-gray-600">
                    <p>
                      You can manage your bank accounts, including adding new accounts, updating existing ones, or deleting accounts, from the 
                      <inertia-link :href="route('user-bank-accounts.index')" class="text-blue-600 hover:text-blue-800 underline">
                        Bank Accounts page
                      </inertia-link>.
                    </p>
                  </div>
                </div>

                <div class="sm:col-span-2">
                  <form-button :form="form" type="submit" :disabled="!is_kyc || !user_bank_account || user_action_required || (user_bank_account && user_bank_account.status === 'needs_update')"
                    :class="!is_kyc || !user_bank_account || user_action_required || (user_bank_account && user_bank_account.status === 'needs_update') ? 'cursor-not-allowed' : ''"
                    class="!-ml-0 w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-100">
                    Proceed to Payment
                  </form-button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
    <full-page-loader v-if="isLoader"></full-page-loader>
  </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import FormButton from '@/Shared/FormButton.vue'
import SmallCards from "@/Shared/SmallCards.vue";
import axios from "axios";
import Button from "@/Jetstream/Button.vue";
// import {Inertia} from "@inertiajs/inertia";
import FullPageLoader from "@/Shared/FullPageLoader.vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronLeft, faDollarSign } from '@fortawesome/pro-solid-svg-icons'

export default {
  props: {
    pay_data: Array,
    is_kyc: Boolean,
    bank_account: Object,
    link_tokens: Array,
    user_action_required: Boolean,
    today_date: Date,
  },

  components: {
    FullPageLoader,
    Button,
    TailwindLayout,
    FormButton,
    SmallCards,
    FontAwesome: FontAwesomeIcon
  },
  data() {
    return {
      user_bank_account: this.bank_account,
      button_active_state: false,
      plaid_fee: 0.00,
      originalPlaidFee: 0.00,
      total: 0.00,
      form: this.$inertia.form({
        rented_property_id: this.pay_data.rented_property_id,
        type: this.pay_data.type,
        value: this.pay_data.value,
        amount: this.pay_data.amount,
        subtotal: this.pay_data.formatted_subtotal,
        remaining: 0,
        bank_account_id: this.bank_account ? this.bank_account.id : null,
        customer_mandate: false,
      }),
      isLoader: false,
      showPlaidContainer: false,
      faChevronLeft,
    }
  },
  mounted() {
    this.calculateCardFeeAndTotal();

    if (this.is_kyc && !this.user_bank_account) {
      if (this.link_tokens.auth) {
        this.initializePlaidLinkAuth(this.link_tokens.auth);
      }
    }

  },
  watch: {
    'form.amount': function (newValue, oldValue) {
      if (newValue === null || newValue === '') {
        this.form.errors.amount = 'The amount field is required.';
        this.button_active_state = true;
        this.form.remaining = this.pay_data.amount;
        return false;
      }
      if (newValue > this.pay_data.amount) {
        this.form.errors.amount = 'The amount cannot be greater than ' + this.formatCurrency(this.pay_data.amount);
        this.button_active_state = true;
        this.form.remaining = 0.00;
        return false;
      }
      this.button_active_state = false;
      this.form.errors.amount = null;
      this.calculateCardFeeAndTotal();
      this.form.remaining = (this.pay_data.amount - this.form.amount).toFixed(2);
      this.form.subtotal = (parseFloat(this.pay_data.extra_charges.amount) + parseFloat(this.form.amount)).toFixed(2);
    }
  },
  methods: {
    formatCurrency(amount) {
      if (!amount) {
        amount = 0;
      }
      amount = parseFloat(amount);
      return amount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    },
    store() {
      if (!this.form.customer_mandate) {
        this.form.errors.customer_mandate = 'Please accept this field in order to proceed with the payment.';
        return false;
      }
      this.form.post(this.route('rented-property.store-pay'), {
        onSuccess: (response) => {
        }
      })
    },
    async calculateCardFeeAndTotal() {
      try {
        let amount = this.form.amount
        if (this.pay_data.extra_charges.amount) {
          amount = parseFloat(this.pay_data.extra_charges.amount) + parseFloat(this.form.amount)
        }
        const response = await axios.post(this.route('calculate-card-fee-plus-total'), {
          amount: amount,
          network: this.pay_data.network,
          rented_property_id: this.pay_data.rented_property_id
        });
        if (response.data.status) {
          this.originalPlaidFee = response.data.original_plaid_fee || response.data.plaid_fee;
          this.plaid_fee = response.data.plaid_fee;
          this.total = response.data.total;
          // this.form.subtotal = response.data.subtotal;
          // this.pay_data.extra_charges.amount = response.data.extra_charges;
        }
      } catch (error) {
      }
    },

    // plaid work

    async createLinkToken(product) {
      this.isLoader = true;
      try {
        const response = await axios.post(route('plaid.create-link-token'), {
          product: product,
        });
        if (response.data.status) {
          this.initializePlaidLink(response.data.public_token, product);
        }

        // Handle the successful response here
      } catch (error) {
        // Handle any errors here
      }
    },

    async initializePlaidLinkIdv(link_token) {
      this.isLoader = true;
      var handler = Plaid.create({
        token: link_token,  // Use the provided public token directly
        onLoad: function () {
          // Optional, called when Link loads
        },
        onSuccess: (public_token, metadata) => {
          this.isLoader = true;
          this.finishIdv(public_token, metadata);
        },
        onExit: function (err, metadata) {
          // The user exited the Link flow.
          if (err != null) {
            // The user encountered a Plaid API error prior to exiting.
          }
          // metadata contains information about the institution
          // that the user selected and the most recent API request IDs.
          // Storing this information can be helpful for support.
        },
        onEvent: function (eventName, metadata) {
          if (eventName === 'IDENTITY_VERIFICATION_CLOSE_UI') {
            location.reload();
          }
          // Optionally capture Link flow events, streamed through
          // this callback as your users connect an Item to Plaid.
          // For example:
          // eventName = "TRANSITION_VIEW"
          // metadata  = {
          //   link_session_id: "123-abc",
          //   mfa_type:        "questions",
          //   timestamp:       "2017-09-14T14:42:19.350Z",
          //   view_name:       "MFA",
          // }
        }
      },
        // this.$refs.plaidContainer
      );

      handler.open();
    },

    async initializePlaidLinkAuth(link_token) {
      this.showPlaidContainer = true;
      var handler = Plaid.createEmbedded({
        token: link_token,  // Use the provided public token directly
        onLoad: function () {
          // Optional, called when Link loads
        },
        onSuccess: (public_token, metadata) => {
          this.isLoader = true;
          this.finishAuth(public_token, metadata);
        },
        onExit: function (err, metadata) {
          // The user exited the Link flow.
          if (err != null) {
            // The user encountered a Plaid API error prior to exiting.
          }
          // metadata contains information about the institution
          // that the user selected and the most recent API request IDs.
          // Storing this information can be helpful for support.
        },
        onEvent: function (eventName, metadata) {
          // Optionally capture Link flow events, streamed through
          // this callback as your users connect an Item to Plaid.
          // For example:
          // eventName = "TRANSITION_VIEW"
          // metadata  = {
          //   link_session_id: "123-abc",
          //   mfa_type:        "questions",
          //   timestamp:       "2017-09-14T14:42:19.350Z",
          //   view_name:       "MFA",
          // }
        }
      },
        this.$refs.plaidContainer
      );
    },


    async finishAuth(public_token, metadata) {
      const response = await axios.post(route('plaid.exchange-token-and-retrieve-account'), {
        public_token: public_token,
        metadata: metadata,
      });
      this.isLoader = false;

      if (response.data.status) {
        this.user_bank_account = response.data.user_bank_account;
        this.form.bank_account_id = response.data.user_bank_account.id;
      }

      location.reload();
    },
    async finishIdv(public_token, metadata) {
      const response = await axios.post(route('plaid.idv.submit'), {
        public_token: public_token,
        metadata: metadata,
      });
      this.isLoader = false;
    }
  }
}
</script>
